import { Avatar, Box, Chip, Typography, buttonBaseClasses, useMediaQuery, useTheme } from '@mui/material';
import { format } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Business_Profile_Enum, Order_Status_Enum, Payment_Method_Enum } from '@kheops/graphql';
import { orderAtom, orderInvoiceAtom } from '../state/state';
import { currentContextAtom, userAtom } from '../../state';
import ExpandableText from '../../common/components/ExpandableText';
import { OrderButtonType } from '../../common/models/order.models';
import { longDateFormat } from '../../common/state/state';
import { dateAsRelative } from '../../common/utils/date.utils';
import useCurrencyFormat from '../../hooks/useCurrencyFormat';
import PhotosUtils from '../../common/utils/photos.utils';
import OrderTaxFreeTotalPriceDisplay from './OrderTaxFreeTotalPriceDisplay';
import useOrderFormButtons from './hooks/useOrderFormButtons';

interface OrderInvoiceDetailsProps {
  availableButtons: OrderButtonType[];
}

export default function OrderInvoiceDetails({ availableButtons }: OrderInvoiceDetailsProps): React.JSX.Element {
  const currencyFormat = useCurrencyFormat();
  const { t } = useTranslation(['order', 'price', 'payment']);
  const invoice = useAtomValue(orderInvoiceAtom);
  const { realm } = useAtomValue(currentContextAtom);
  const order = useAtomValue(orderAtom);
  const { id: userId } = useAtomValue(userAtom);
  const orderFormButtons = useOrderFormButtons({ availableButtons, userId });
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const canEditInvoice = realm === Business_Profile_Enum.Supplier && order.status === Order_Status_Enum.OrderToBePaidByBuyer && !order.has_invoice_generation;
  const displayedReference = invoice?.revised_reference || invoice?.reference;

  const displayButtonInTopRightCorner = orderFormButtons.length === 1 && !canEditInvoice && !isXs;

  return (
    <Box
      sx={{
        backgroundColor: 'surfaceContainerHigh.main',
        py: 3,
        px: 2,
        borderRadius: 6,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px 48px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography variant="bodySmall">{t('order:invoice_number')}</Typography>
            <Typography variant="bodyMedium">{displayedReference}</Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography variant="bodySmall">{t('price:price_without_tax')}</Typography>
            <OrderTaxFreeTotalPriceDisplay />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography variant="bodySmall">{t('price:price_tax_included')}</Typography>
            <Typography variant="bodyMedium">{currencyFormat.format(order.total_price_to_pay_incl_tax!)}</Typography>
          </Box>
          {(order.payment_method === Payment_Method_Enum.SddAuto || order.payment_method === Payment_Method_Enum.SddTriggered) && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              <Typography variant="bodySmall">{t('payment:payment_method')}</Typography>
              <Chip
                variant="tag"
                color="surfaceContainerHighest"
                label={t(order.payment_method === Payment_Method_Enum.SddAuto ? 'payment:auto_payment' : 'payment:triggered_payment')}
              />
            </Box>
            {order.sdd_payment_date && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Typography variant="bodySmall">{t('payment:payment_date')}</Typography>
              <Typography variant="bodyMedium">{format(order.sdd_payment_date, longDateFormat)}</Typography>
            </Box>
            )}
          </>
          )}
        </Box>

        {displayButtonInTopRightCorner && (
          <Box
            sx={{
              height: 40,
              [`& .${buttonBaseClasses.root}`]: {
                lineHeight: '18px',
              },
            }}
          >
            {orderFormButtons}
          </Box>
        )}
      </Box>
      {invoice?.comment && (
        <>
          <Typography variant="titleMedium">{t('order:total_change_reason')} :</Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              p: 2,
              backgroundColor: 'surfaceContainerHighest.main',
              borderRadius: 4,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                }}
              >
                <Avatar src={PhotosUtils.getUserAvatarPicture(invoice.order!.updater!)} size={32} variant="square" />
                <Box>
                  <Typography variant="bodySmall">{invoice.order!.updater?.first_name} {invoice.order!.updater?.last_name}</Typography>
                  <Typography variant="bodySmall" color="secondary">{dateAsRelative(new Date(invoice.modified_date))}</Typography>
                </Box>
              </Box>
              <ExpandableText
                text={invoice.comment}
                lineClampThreshold={4}
              />
            </Box>
          </Box>
        </>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          [`& .${buttonBaseClasses.root}`]: {
            height: '42px',
          },
          flexDirection: {
            xs: 'column',
            md: 'row',
          },
          gap: 1,
        }}
      >
        {!displayButtonInTopRightCorner && (orderFormButtons)}
      </Box>
    </Box>
  );
}
